import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { GigContract as GigContractType } from "model/GigContract";
import { Api } from "services/api/api.service";
import { getDateString, getWorkHours } from "utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ContractActions,
  ContractContent,
  ContractDescriptionField,
  ContractFieldRow,
  ContractFields,
  ContractLoading,
  ContractSection,
  ContractSpinner,
} from "./gigContract.styled";

interface Props {
  readonly applicationId: number | null;
  readonly closeContract: () => void;
  readonly showCloseAction?: boolean;
}

interface ContractFieldProps {
  readonly label: string;
  readonly value: React.ReactNode;
}

const ContractField = (props: ContractFieldProps): JSX.Element => {
  return (
    <ContractFieldRow>
      <Text typography="s1" color={ColorV2.Blue4}>
        {props.label}
      </Text>
      <Text typography="b2" color={ColorV2.Blue1}>
        {props.value}
      </Text>
    </ContractFieldRow>
  );
};

type ContractState =
  | { readonly type: "loading" }
  | {
      readonly type: "success";
      readonly value: GigContractType & { readonly displayGigDuration: string };
    }
  | { readonly type: "error" };

export const GigContract: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [contractState, setContractState] = useState<ContractState>({
    type: "loading",
  });

  useEffect(() => {
    const companyId = companyState.company?.id;
    if (!companyId || !props.applicationId) {
      setContractState({ type: "error" });
      return;
    }
    let isCurrentRequest = true;
    setContractState({ type: "loading" });
    Api()
      .company.gig.getGigContract(companyId, props.applicationId)
      .then((res) => {
        if (isCurrentRequest) {
          setContractState({
            type: "success",
            value: {
              ...res.data,
              displayGigDuration: getWorkHours(t, ...res.data.gig.shifts),
            },
          });
        }
      })
      .catch(() => {
        if (isCurrentRequest) setContractState({ type: "error" });
      });
    return () => {
      isCurrentRequest = false;
    };
  }, [companyState.company?.id, props.applicationId, t]);

  if (contractState.type === "loading") {
    return (
      <ContractLoading>
        <ContractSpinner aria-label={t("General.Loading")} role="status" />
      </ContractLoading>
    );
  }

  if (contractState.type === "error") {
    return (
      <Text typography="b2" color={ColorV2.NegativeRed}>
        {t("General.ErrorOccurred")}
      </Text>
    );
  }

  const contractInfo = contractState.value;

  const firstShift = contractInfo.gig.shifts[0];
  const lastShift = contractInfo.gig.shifts[contractInfo.gig.shifts.length - 1];
  const approval = contractInfo.application.acceptedAt
    ? `${getDateString(contractInfo.application.acceptedAt)}, ${
        contractInfo.company.city
      }`
    : "-";

  return (
    <ContractContent>
      {(props.showCloseAction ?? true) && (
        <ContractActions>
          <SecondaryButton
            label={t("ProfileCompany.Done")}
            size="small"
            onClick={props.closeContract}
          />
        </ContractActions>
      )}

      <ContractSection>
        <Text typography="h5" color={ColorV2.Blue1}>
          {t("General.Employee")}
        </Text>
        <ContractFields>
          <ContractField
            label={t("ProfileWorker.FirstName")}
            value={contractInfo.worker.firstName}
          />
          <ContractField
            label={t("ProfileWorker.LastName")}
            value={contractInfo.worker.lastName}
          />
          <ContractField
            label={t("ProfileWorker.PersonalNumber")}
            value={contractInfo.worker.ssn}
          />
          <ContractField
            label={t("ProfileWorker.Address")}
            value={contractInfo.worker.address}
          />
          <ContractField
            label={t("ProfileWorker.PostalCode")}
            value={contractInfo.worker.postalCode}
          />
          <ContractField
            label={t("ProfileWorker.City")}
            value={contractInfo.worker.city}
          />
        </ContractFields>
      </ContractSection>

      <ContractSection>
        <Text typography="h5" color={ColorV2.Blue1}>
          {t("General.Employer")}
        </Text>
        <ContractFields>
          <ContractField
            label={t("ProfileCompany.LegalName")}
            value={contractInfo.company.companyName}
          />
          <ContractField
            label={t("ProfileCompany.RegistrationNumber")}
            value={contractInfo.company.registrationNumber}
          />
          <ContractField
            label={t("ProfileCompany.Address")}
            value={contractInfo.company.address}
          />
          <ContractField
            label={t("ProfileCompany.PostalCode")}
            value={contractInfo.company.postalCode}
          />
          <ContractField
            label={t("ProfileCompany.City")}
            value={contractInfo.company.city}
          />
        </ContractFields>
      </ContractSection>

      <ContractSection>
        <Text typography="h5" color={ColorV2.Blue1}>
          {t("General.GigDetails")}
        </Text>
        <ContractFields>
          <ContractField
            label={t("GigDetails.Role")}
            value={contractInfo.gig.role}
          />
          <ContractDescriptionField>
            <Text typography="s1" color={ColorV2.Blue4}>
              {t("GigDetails.Description")}
            </Text>
            <Text typography="b2" color={ColorV2.Blue1}>
              {contractInfo.gig.description}
            </Text>
          </ContractDescriptionField>
          <ContractField
            label={t("GigDetails.StartDate")}
            value={firstShift ? getDateString(firstShift.startTime) : "-"}
          />
          <ContractField
            label={`${t("GigsWorker.HourlyRate")} (${t("General.Currency")})`}
            value={`${contractInfo.application.requestedRate} (${t(
              "GigDetails.HolidayPayIncluded",
            )})`}
          />
          <ContractField
            label={t("GigDetails.TotalHours")}
            value={contractInfo.displayGigDuration}
          />
          <ContractField
            label={t("GigDetails.FormOfEmployment")}
            value={t("GigDetails.IntermittentEmployment")}
          />
          <ContractField
            label={t("GigDetails.EndDate")}
            value={lastShift ? getDateString(lastShift.startTime) : "-"}
          />
          <ContractField
            label={t("GigDetails.ApprovedByBothParties")}
            value={approval}
          />
        </ContractFields>
      </ContractSection>
    </ContractContent>
  );
};
